import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { KNOWN_HISTORY_TYPES } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.model";
import type { QueryRenderResult } from "src/domains/shared/utils/queryRenderState";
import type { ProjectOption } from "../hooks/useProjectHistory";
import { CAPTION } from "src/domains/shared/components/StateTransition";
import { ValueSelect, ValueSelectOption } from "src/domains/shared/components/ValueSelect";

interface HistoryFilterBarProps {
  projectOptions: ProjectOption[];
  /**
   * 선택된 프로젝트 id 문자열. 미선택은 빈 문자열.
   * `projectOptions` 에 없는 값이면(미선택 포함) 입력칸이 비고 placeholder 가 보인다.
   */
  selectedProject: string;
  onSelectProject: (projectId: string) => void;
  /** 선택된 이력 종류. `null` 이 "전체"(API 파라미터 생략)를 의미한다. */
  historyTypeFilter: string | null;
  onSelectHistoryType: (historyType: string | null) => void;
  /**
   * 프로젝트 목록 조회의 렌더 갈래 — 패널(`ProjectHistory`)이 실패·빈 상태를 가르는 데 쓰는
   * 것과 **같은 값**이다. 이 바가 자체 로딩 판정을 다시 세우면 위쪽 실패 화면과 어긋난다.
   */
  projectsRenderState: QueryRenderResult;
}

const TYPE_SELECT_WIDTH_PX = 148;

/**
 * 프로젝트 히스토리 상단의 필터 줄 — 라벨을 단 프로젝트 검색 콤보박스와 이력 종류 드롭다운.
 * 종류 옵션 구성이 이 파일 안에서 끝난다.
 */
export const HistoryFilterBar: FC<HistoryFilterBarProps> = ({
  projectOptions,
  selectedProject,
  onSelectProject,
  historyTypeFilter,
  onSelectHistoryType,
  projectsRenderState,
}) => {
  const { t } = useTranslation("projects");

  const typeFilterOptions: ValueSelectOption<string | null>[] = [
    { label: t("constants.all", { ns: "common" }), value: null },
    ...KNOWN_HISTORY_TYPES.map((type) => ({
      label: t(`myPage.history.type.${type}`, { ns: "projects" }),
      value: type,
    })),
  ];

  const selectProjectLabel = t("projectPermission.selectProject", { ns: "projects" });
  // 고를 수 있는 프로젝트가 없으면 두 칸 모두 조작할 것이 없다 — 이력 종류도 대상 프로젝트가 있어야 의미가 있다.
  const hasNoProjects = projectOptions.length === 0;
  // "조회 중" 은 아직 종결되지 않은 첫 조회만 가리킨다. 실패로 종결된 구간까지 덮으면 위쪽 실패 갈래와 어긋난다.
  const isLoadingFirstProjects = projectsRenderState.state === "pending" && hasNoProjects;

  const filterLabel = t("constants.filter", { ns: "common" });

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        gap: "8px",
        padding: "10px 12px 12px",
        borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
        {/* 콤보박스가 같은 접근 이름을 가지므로 보이는 라벨은 보조 기술에서 숨긴다. */}
        <Typography aria-hidden="true" sx={filterLabelSx}>
          {selectProjectLabel}
        </Typography>
        <ValueSelect
          searchable
          ariaLabel={selectProjectLabel}
          options={projectOptions}
          value={selectedProject}
          onChange={onSelectProject}
          disabled={hasNoProjects}
          placeholder={isLoadingFirstProjects ? t("projectPermission.loading", { ns: "projects" }) : selectProjectLabel}
        />
      </Box>
      <Box sx={{ width: TYPE_SELECT_WIDTH_PX, flexShrink: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
        <Typography aria-hidden="true" sx={filterLabelSx}>
          {filterLabel}
        </Typography>
        <ValueSelect
          ariaLabel={filterLabel}
          options={typeFilterOptions}
          value={historyTypeFilter}
          onChange={onSelectHistoryType}
          disabled={hasNoProjects}
          placeholder={t("constants.all", { ns: "common" })}
        />
      </Box>
    </Box>
  );
};

const filterLabelSx = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: "16px",
  color: CAPTION,
} as const;
